import { Crown, Coins } from "lucide-react";
import { useStore } from "@/store/useStore";
import { GOLD_PURITIES, SILVER_PURITIES, SILVER_UNITS } from "@/lib/constants";
import { formatPurity } from "@/lib/formatPurity";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useState } from "react";

export function MobileMetalPuritySelector() {
  const { 
    goldPurity, 
    silverPurity, 
    silverUnit, 
    setGoldPurity, 
    setSilverPurity, 
    setSilverUnit 
  } = useStore();
  
  const [activeMetal, setActiveMetal] = useState<'gold' | 'silver'>('gold');

  return (
    <div className="space-y-4 py-4 border-t border-border/30">
      <div className="px-2">
        <h3 className="text-sm font-semibold text-foreground mb-3">Preferenze Metallo</h3>
        
        {/* Metal Selection */}
        <div className="space-y-3">
          <RadioGroup
            value={activeMetal}
            onValueChange={(value: string) => setActiveMetal(value as 'gold' | 'silver')}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem 
                value="gold" 
                id="mobile-gold"
                className="border-gold data-[state=checked]:border-gold data-[state=checked]:bg-gold/10"
              />
              <Label 
                htmlFor="mobile-gold" 
                className="flex items-center gap-2 cursor-pointer text-gold font-medium"
              >
                <Crown className="h-4 w-4" />
                Oro
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem 
                value="silver" 
                id="mobile-silver"
                className="border-silver data-[state=checked]:border-silver data-[state=checked]:bg-silver/10"
              />
              <Label 
                htmlFor="mobile-silver" 
                className="flex items-center gap-2 cursor-pointer text-silver font-medium"
              >
                <Coins className="h-4 w-4" />
                Argento
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Purity Selection */}
        {activeMetal === 'gold' ? (
          <div className="space-y-3 mt-4">
            <Label className="text-sm font-medium text-gold">Purezza Oro</Label>
            <Select value={goldPurity} onValueChange={setGoldPurity}>
              <SelectTrigger className="border-gold/30 focus:border-gold focus:ring-gold/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(GOLD_PURITIES).map(([key, { label }]) => (
                  <SelectItem 
                    key={key} 
                    value={key}
                    className="focus:bg-gold/10 focus:text-gold"
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium text-silver">Purezza Argento</Label>
              <Select value={silverPurity} onValueChange={setSilverPurity}>
                <SelectTrigger className="border-silver/30 focus:border-silver focus:ring-silver/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SILVER_PURITIES).map(([key, { label }]) => (
                    <SelectItem 
                      key={key} 
                      value={key}
                      className="focus:bg-silver/10 focus:text-silver"
                    >
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium text-silver">Unità</Label>
              <Select value={silverUnit} onValueChange={setSilverUnit}>
                <SelectTrigger className="border-silver/30 focus:border-silver focus:ring-silver/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SILVER_UNITS.map((unit) => (
                    <SelectItem 
                      key={unit} 
                      value={unit}
                      className="focus:bg-silver/10 focus:text-silver"
                    >
                      {unit === 'g' ? 'Grammi (g)' : 'Chilogrammi (kg)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-border/20">
          <p className="text-xs text-muted-foreground">
            Impostazioni globali per {formatPurity(activeMetal, activeMetal === 'gold' ? goldPurity : silverPurity)}
          </p>
        </div>
      </div>
    </div>
  );
}